import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import {
  DEFAULT_WIDGET_DISPLAY_OPTIONS,
  REPORT_SCHEMA_VERSION,
  duplicateStudioReport,
  type GlobalReportFilter,
  type ReportLayoutItem,
  type ReportWidget,
  type StudioReport,
} from "../../domain/reporting/report-definition";
import { parseStudioReport } from "../../domain/reporting/report-migrations";
import {
  globalReportFilterSchema,
  parseCurrentStudioReport,
  reportLayoutItemSchema,
  reportWidgetSchema,
  studioReportDateRangeSchema,
} from "../../domain/reporting/report-schema";
import { getFirebaseServices } from "./firebase";
import { readCloudConfig } from "./config-repository";
import { recordRevision, submitSyncV3Mutations } from "./sync-v3-client";

type UnknownRecord = Record<string, unknown>;

export interface ReportSummary {
  id: string;
  name: string;
  description?: string;
  widgetCount: number;
  createdAt: string;
  updatedAt: string;
}

export class ReportAlreadyExistsError extends Error {
  constructor(reportId: string) {
    super(`A report with the identifier ${reportId} already exists.`);
    this.name = "ReportAlreadyExistsError";
  }
}

export class ReportNotFoundError extends Error {
  constructor(reportId: string) {
    super(`The report ${reportId} no longer exists.`);
    this.name = "ReportNotFoundError";
  }
}

export class ReportConflictError extends Error {
  readonly current: StudioReport;

  constructor(current: StudioReport) {
    super("This report changed in another Studio session. Review the latest version before saving again.");
    this.name = "ReportConflictError";
    this.current = current;
  }
}

export class MalformedReportError extends Error {
  readonly reportId: string;

  constructor(reportId: string, cause: unknown) {
    super(`The saved report ${reportId} could not be read.`, { cause });
    this.name = "MalformedReportError";
    this.reportId = reportId;
  }
}

function assertCurrentUser(uid: string): void {
  if (!uid || getFirebaseServices().auth.currentUser?.uid !== uid) {
    throw new Error("The requested workspace does not belong to the signed-in account.");
  }
}

function assertReportId(reportId: string): void {
  if (!reportId.trim() || reportId.includes("/")) {
    throw new Error("Invalid report identifier.");
  }
}

function reportCollection(uid: string) {
  assertCurrentUser(uid);
  return collection(getFirebaseServices().firestore, "users", uid, "workspaceRecords");
}

function reportDocument(uid: string, reportId: string) {
  assertCurrentUser(uid);
  assertReportId(reportId);
  return doc(getFirebaseServices().firestore, "users", uid, "workspaceRecords", `studio-report:${reportId}`);
}

function legacyReportCollection(uid: string) {
  assertCurrentUser(uid);
  return collection(getFirebaseServices().firestore, "users", uid, "studioReports");
}

function legacyReportDocument(uid: string, reportId: string) {
  assertCurrentUser(uid);
  assertReportId(reportId);
  return doc(getFirebaseServices().firestore, "users", uid, "studioReports", reportId);
}

function record(value: unknown): UnknownRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as UnknownRecord
    : {};
}

function isoTimestamp(value: unknown): string | undefined {
  if (typeof value === "string" && !Number.isNaN(Date.parse(value))) return value;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (value && typeof value === "object" && "toDate" in value) {
    const toDate = (value as { toDate?: unknown }).toDate;
    if (typeof toDate === "function") {
      try {
        const date = (toDate as (this: unknown) => Date).call(value);
        if (date instanceof Date && !Number.isNaN(date.getTime())) return date.toISOString();
      } catch {
        return undefined;
      }
    }
  }
  return undefined;
}

function migrationTimestamp(raw: UnknownRecord): string {
  return isoTimestamp(raw.updatedAt)
    ?? isoTimestamp(raw.createdAt)
    ?? "1970-01-01T00:00:00.000Z";
}

function migrationIdFactory(reportId: string): () => string {
  let index = 0;
  return () => `${reportId}-migrated-${++index}`;
}

const UNAVAILABLE_WIDGET_REASON = "This section uses a reporting field or setting that is no longer available. Edit it to choose a current configuration, or remove it from the report.";

function recoveredWidgetId(reportId: string, index: number, usedIds: Set<string>, candidate: unknown): string {
  const preferred = typeof candidate === "string" && candidate.trim() && !candidate.includes("/")
    ? candidate.trim()
    : `${reportId}-unavailable-${index + 1}`;
  let value = preferred;
  let suffix = 1;
  while (usedIds.has(value)) value = `${preferred}-${++suffix}`;
  return value;
}

function recoverWidgets(value: unknown, reportId: string): {
  readonly widgets: ReportWidget[];
  readonly recoveredCount: number;
} {
  if (!Array.isArray(value)) return { widgets: [], recoveredCount: 0 };
  const widgets: ReportWidget[] = [];
  const usedIds = new Set<string>();
  let recoveredCount = 0;
  for (const [index, item] of value.entries()) {
    const parsed = reportWidgetSchema.safeParse(item);
    if (parsed.success && !usedIds.has(parsed.data.id)) {
      usedIds.add(parsed.data.id);
      widgets.push(parsed.data as ReportWidget);
      continue;
    }

    const raw = record(item);
    const id = recoveredWidgetId(reportId, index, usedIds, raw.id);
    const title = typeof raw.title === "string" && raw.title.trim() && raw.title.trim().length <= 200
      ? raw.title.trim()
      : "Unavailable section";
    usedIds.add(id);
    recoveredCount += 1;
    widgets.push({
      id,
      type: "text",
      title,
      dataSource: "cases",
      filters: [],
      calculation: null,
      visualisation: { type: "text" },
      displayOptions: DEFAULT_WIDGET_DISPLAY_OPTIONS,
      textContent: "",
      unavailableReason: UNAVAILABLE_WIDGET_REASON,
    });
  }
  return { widgets, recoveredCount };
}

function recoverGlobalFilters(value: unknown): {
  readonly filters: GlobalReportFilter[];
  readonly discardedCount: number;
} {
  if (!Array.isArray(value)) return { filters: [], discardedCount: 0 };
  const filters: GlobalReportFilter[] = [];
  let discardedCount = 0;
  for (const item of value) {
    const parsed = globalReportFilterSchema.safeParse(item);
    if (parsed.success) filters.push(parsed.data as GlobalReportFilter);
    else discardedCount += 1;
  }
  return { filters, discardedCount };
}

function recoverLayout(value: unknown, widgets: readonly ReportWidget[]): ReportLayoutItem[] {
  const widgetIds = new Set(widgets.map((widget) => widget.id));
  const layout: ReportLayoutItem[] = [];
  const usedIds = new Set<string>();
  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = reportLayoutItemSchema.safeParse(item);
      if (!parsed.success || !widgetIds.has(parsed.data.widgetId) || usedIds.has(parsed.data.widgetId)) continue;
      layout.push({
        widgetId: parsed.data.widgetId,
        x: parsed.data.x,
        y: parsed.data.y,
        width: parsed.data.width,
        height: parsed.data.height,
        ...(parsed.data.minimumWidth === undefined ? {} : { minimumWidth: parsed.data.minimumWidth }),
        ...(parsed.data.minimumHeight === undefined ? {} : { minimumHeight: parsed.data.minimumHeight }),
      });
      usedIds.add(parsed.data.widgetId);
    }
  }
  let nextY = layout.reduce((maximum, item) => Math.max(maximum, item.y + item.height), 0);
  for (const widget of widgets) {
    if (usedIds.has(widget.id)) continue;
    layout.push({ widgetId: widget.id, x: 0, y: nextY, width: 12, height: 6, minimumWidth: 4, minimumHeight: 3 });
    usedIds.add(widget.id);
    nextY += 6;
  }
  return layout;
}

/**
 * A current report with one obsolete widget should still open. Invalid widgets
 * become explicit, editable placeholders; valid sections are preserved.
 */
function recoverCurrentReport(value: unknown, reportId: string, uid: string): StudioReport | null {
  const raw = record(value);
  if (raw.schemaVersion !== REPORT_SCHEMA_VERSION) return null;
  if (raw.id !== undefined && raw.id !== reportId) return null;
  if (raw.ownerId !== undefined && raw.ownerId !== uid) return null;
  if (typeof raw.name !== "string" || !raw.name.trim()) return null;
  const createdAt = isoTimestamp(raw.createdAt);
  const updatedAt = isoTimestamp(raw.updatedAt);
  if (!createdAt || !updatedAt) return null;

  const recoveredWidgets = recoverWidgets(raw.widgets, reportId);
  const recoveredFilters = recoverGlobalFilters(raw.globalFilters);
  const parsedDateRange = raw.dateRange === undefined
    ? undefined
    : studioReportDateRangeSchema.safeParse(raw.dateRange);
  const scopeNeedsAttention = recoveredFilters.discardedCount > 0
    || (parsedDateRange !== undefined && !parsedDateRange.success);
  const widgets = [...recoveredWidgets.widgets];
  if (scopeNeedsAttention) {
    const usedIds = new Set(widgets.map((widget) => widget.id));
    const id = recoveredWidgetId(reportId, widgets.length, usedIds, `${reportId}-scope-unavailable`);
    widgets.unshift({
      id,
      type: "text",
      title: "Report filters need attention",
      dataSource: "cases",
      filters: [],
      calculation: null,
      visualisation: { type: "text" },
      displayOptions: DEFAULT_WIDGET_DISPLAY_OPTIONS,
      textContent: "",
      unavailableReason: "One or more saved report filters are no longer available and were not applied. Review the report date range and whole-report filters before saving.",
    });
  }

  return parseCurrentStudioReport({
    id: reportId,
    ownerId: uid,
    name: raw.name,
    ...(typeof raw.description === "string" && raw.description.trim() ? { description: raw.description } : {}),
    schemaVersion: REPORT_SCHEMA_VERSION,
    dataSource: "cases",
    globalFilters: recoveredFilters.filters,
    ...(parsedDateRange?.success ? { dateRange: parsedDateRange.data } : {}),
    widgets,
    layout: recoverLayout(raw.layout, widgets),
    createdAt,
    updatedAt,
  });
}

/**
 * Firestore document identity and ownership are authoritative only when an old
 * definition did not persist them. A conflicting persisted value is rejected.
 */
function decodeReport(value: unknown, reportId: string, uid: string): StudioReport {
  try {
    const envelope = record(value);
    const logicalReportId = envelope.recordType === "studioReport"
      && typeof envelope.reportId === "string" && envelope.reportId.trim()
      ? envelope.reportId
      : reportId.startsWith("studio-report:") ? reportId.slice("studio-report:".length) : reportId;
    const raw = envelope.recordType === "studioReport" ? record(envelope.data) : envelope;
    const candidate: UnknownRecord = { ...raw };
    if (!Object.hasOwn(candidate, "id")) candidate.id = logicalReportId;
    if (!Object.hasOwn(candidate, "ownerId")) candidate.ownerId = uid;
    const createdAt = isoTimestamp(candidate.createdAt);
    // v3 revisions are server-owned. Keep that timestamp on the report too,
    // so the editor's optimistic-concurrency check is never based on a clock.
    const updatedAt = isoTimestamp(envelope.updatedAt) ?? isoTimestamp(candidate.updatedAt);
    if (createdAt) candidate.createdAt = createdAt;
    if (updatedAt) candidate.updatedAt = updatedAt;

    let parsed: StudioReport;
    try {
      parsed = parseStudioReport(candidate, {
        ownerId: uid,
        now: migrationTimestamp(raw),
        idFactory: migrationIdFactory(logicalReportId),
      });
    } catch (reason) {
      const recovered = recoverCurrentReport(candidate, logicalReportId, uid);
      if (!recovered) throw reason;
      parsed = recovered;
    }
    if (parsed.id !== logicalReportId) throw new Error("The report identifier does not match its document path.");
    if (parsed.ownerId !== uid) throw new Error("The report owner does not match the signed-in account.");
    return parsed;
  } catch (reason) {
    if (reason instanceof MalformedReportError) throw reason;
    throw new MalformedReportError(reportId.startsWith("studio-report:") ? reportId.slice("studio-report:".length) : reportId, reason);
  }
}

function firestoreValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(firestoreValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, firestoreValue(item)]),
    );
  }
  return value;
}

function encodeReport(value: StudioReport, uid: string): UnknownRecord {
  if (value.ownerId !== uid) {
    throw new Error("The report does not belong to the signed-in account.");
  }
  const parsed = parseCurrentStudioReport(value);
  return firestoreValue(parsed) as UnknownRecord;
}

function encodeWorkspaceReport(value: StudioReport, uid: string): UnknownRecord {
  return { recordType: "studioReport", reportId: value.id, data: encodeReport(value, uid) };
}

function resultReport(value: unknown, reportId: string, uid: string): StudioReport | null {
  return value && typeof value === "object" ? decodeReport(value, reportId, uid) : null;
}

function clearedReportDataPaths(previous: unknown, next: unknown, prefix = "data"): string[] {
  const oldRecord = record(previous);
  const nextRecord = record(next);
  const cleared: string[] = [];
  for (const [key, oldValue] of Object.entries(oldRecord)) {
    if (Object.hasOwn(nextRecord, key)) {
      const nextValue = nextRecord[key];
      if (oldValue && nextValue && typeof oldValue === "object" && typeof nextValue === "object"
        && !Array.isArray(oldValue) && !Array.isArray(nextValue)) {
        cleared.push(...clearedReportDataPaths(oldValue, nextValue, `${prefix}.${key}`));
      }
      continue;
    }
    cleared.push(`${prefix}.${key}`);
  }
  return cleared;
}

function errorValue(reason: unknown): Error {
  return reason instanceof Error ? reason : new Error(String(reason));
}

export function summarizeReport(report: StudioReport): ReportSummary {
  return {
    id: report.id,
    name: report.name,
    ...(report.description === undefined ? {} : { description: report.description }),
    widgetCount: report.widgets.length,
    createdAt: report.createdAt,
    updatedAt: report.updatedAt,
  };
}

export async function listReports(uid: string): Promise<StudioReport[]> {
  const snapshot = await getDocs(query(
    reportCollection(uid),
    where("recordType", "==", "studioReport"),
    orderBy("updatedAt", "desc"),
  ));
  return snapshot.docs.map((item) => decodeReport(item.data(), item.id, uid));
}

export interface LegacyReportImportResult {
  readonly imported: number;
  readonly alreadyPresent: number;
  readonly failed: readonly { readonly reportId: string; readonly message: string }[];
}

/**
 * Imports the former config/main.studioSavedReports array without mutating it.
 * Existing documents win, making the operation safe to repeat on every client.
 */
export async function importLegacySavedReports(uid: string): Promise<LegacyReportImportResult> {
  assertCurrentUser(uid);
  const config = await readCloudConfig(uid);
  const legacySnapshot = await getDocs(legacyReportCollection(uid));
  const legacyValues = legacySnapshot.docs.map((item) => ({
    id: item.id,
    ...(record(item.data())),
  }));
  const candidates = [...config.savedReports, ...legacyValues];
  let imported = 0;
  let alreadyPresent = 0;
  const failed: { reportId: string; message: string }[] = [];

  for (const legacy of candidates) {
    try {
      assertReportId(legacy.id);
      const migrated = parseStudioReport(legacy, {
        ownerId: uid,
        now: config.updatedAt || new Date().toISOString(),
        idFactory: migrationIdFactory(legacy.id),
      });
      const existing = await getReport(uid, migrated.id);
      if (existing) {
        alreadyPresent += 1;
      } else {
        await createReport(uid, migrated);
        imported += 1;
      }
    } catch (reason) {
      failed.push({
        reportId: legacy.id || "unknown",
        message: reason instanceof Error ? reason.message : "The saved report could not be upgraded.",
      });
    }
  }

  return { imported, alreadyPresent, failed };
}

export async function getReport(uid: string, reportId: string): Promise<StudioReport | null> {
  const snapshot = await getDoc(reportDocument(uid, reportId));
  if (snapshot.exists()) return decodeReport(snapshot.data(), snapshot.id, uid);
  const legacySnapshot = await getDoc(legacyReportDocument(uid, reportId));
  if (!legacySnapshot.exists()) return null;
  const legacy = decodeReport(legacySnapshot.data(), legacySnapshot.id, uid);
  try {
    return await createReport(uid, legacy);
  } catch (reason) {
    if (!(reason instanceof ReportAlreadyExistsError)) throw reason;
    const migratedSnapshot = await getDoc(reportDocument(uid, reportId));
    return migratedSnapshot.exists() ? decodeReport(migratedSnapshot.data(), migratedSnapshot.id, uid) : null;
  }
}

export function observeReports(
  uid: string,
  observer: (reports: StudioReport[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(
      reportCollection(uid),
      where("recordType", "==", "studioReport"),
      orderBy("updatedAt", "desc"),
    ),
    (snapshot) => {
      const reports: StudioReport[] = [];
      const errors: Error[] = [];
      for (const item of snapshot.docs) {
        try {
          reports.push(decodeReport(item.data(), item.id, uid));
        } catch (reason) {
          errors.push(errorValue(reason));
        }
      }
      observer(reports);
      if (errors[0]) onError?.(errors[0]);
    },
    (reason) => onError?.(errorValue(reason)),
  );
}

export function observeReport(
  uid: string,
  reportId: string,
  observer: (report: StudioReport | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    reportDocument(uid, reportId),
    (snapshot) => {
      try {
        observer(snapshot.exists() ? decodeReport(snapshot.data(), snapshot.id, uid) : null);
      } catch (reason) {
        onError?.(errorValue(reason));
      }
    },
    (reason) => onError?.(errorValue(reason)),
  );
}

export async function createReport(
  uid: string,
  value: StudioReport,
  now = new Date(),
): Promise<StudioReport> {
  assertReportId(value.id);
  assertCurrentUser(uid);
  if (value.ownerId !== uid) {
    throw new Error("The report does not belong to the signed-in account.");
  }
  const created = parseCurrentStudioReport({
    ...value,
    ownerId: uid,
    createdAt: value.createdAt || now.toISOString(),
    updatedAt: value.updatedAt || now.toISOString(),
  });
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "workspaceRecord",
    entityId: `studio-report:${created.id}`,
    operation: "create",
    baseRevision: 0,
    payload: encodeWorkspaceReport(created, uid),
  }]);
  if (result?.status === "applied") {
    const persisted = resultReport(result.canonicalPayload, created.id, uid);
    if (persisted) return persisted;
  }
  if (result?.canonicalPayload) throw new ReportAlreadyExistsError(created.id);
  throw new Error(result?.message ?? "The report could not be created.");
}

export async function updateReport(
  uid: string,
  value: StudioReport,
  expectedUpdatedAt: string,
  now = new Date(),
): Promise<StudioReport> {
  assertReportId(value.id);
  assertCurrentUser(uid);
  if (value.ownerId !== uid) {
    throw new Error("The report does not belong to the signed-in account.");
  }
  const snapshot = await getDoc(reportDocument(uid, value.id));
  if (!snapshot.exists()) throw new ReportNotFoundError(value.id);
  const current = decodeReport(snapshot.data(), snapshot.id, uid);
  if (current.updatedAt !== expectedUpdatedAt) throw new ReportConflictError(current);
  const updated = parseCurrentStudioReport({
    ...value,
    id: current.id,
    ownerId: current.ownerId,
    createdAt: current.createdAt,
    updatedAt: now.toISOString(),
  });
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "workspaceRecord",
    entityId: `studio-report:${updated.id}`,
    operation: "upsert",
    baseRevision: recordRevision(snapshot.data()),
    payload: encodeWorkspaceReport(updated, uid),
    explicitlyClearedPaths: clearedReportDataPaths(record(snapshot.data()).data, encodeReport(updated, uid)),
  }]);
  const persisted = resultReport(result?.canonicalPayload, updated.id, uid);
  if (result?.status === "applied" && persisted) return persisted;
  if (persisted) throw new ReportConflictError(persisted);
  throw new Error(result?.message ?? "The report could not be updated.");
}

export interface DuplicateReportOptions {
  id?: string;
  name?: string;
  now?: Date;
}

export async function duplicateReport(
  uid: string,
  source: StudioReport,
  options: DuplicateReportOptions = {},
): Promise<StudioReport> {
  assertCurrentUser(uid);
  if (source.ownerId !== uid) {
    throw new Error("The report does not belong to the signed-in account.");
  }
  const id = options.id ?? crypto.randomUUID();
  const now = options.now ?? new Date();
  assertReportId(id);
  const duplicate = duplicateStudioReport(source, {
    ownerId: uid,
    name: options.name?.trim() || `${source.name} copy`,
    now: now.toISOString(),
  });
  return createReport(uid, { ...duplicate, id }, now);
}

export async function deleteReport(
  uid: string,
  reportId: string,
  expectedUpdatedAt: string,
): Promise<void> {
  const snapshot = await getDoc(reportDocument(uid, reportId));
  if (!snapshot.exists()) throw new ReportNotFoundError(reportId);
  const current = decodeReport(snapshot.data(), snapshot.id, uid);
  if (current.updatedAt !== expectedUpdatedAt) throw new ReportConflictError(current);
  const [result] = await submitSyncV3Mutations(uid, [{
    entityType: "workspaceRecord",
    entityId: `studio-report:${reportId}`,
    operation: "delete",
    baseRevision: recordRevision(snapshot.data()),
  }]);
  if (result?.status === "applied") return;
  const latest = resultReport(result?.canonicalPayload, reportId, uid);
  if (latest) throw new ReportConflictError(latest);
  throw new Error(result?.message ?? "The report could not be deleted.");
}
